// src/pages/GameFiles/A/puzzle.jsx
import React, { useState, useEffect } from 'react';
import { useGame } from '../../../contexts/GameContext';

const Game2048 = () => {
  const { updateGameState } = useGame();
  const [board, setBoard] = useState(Array(4).fill().map(() => Array(4).fill(0)));
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    initializeGame();
  }, []);

  const initializeGame = () => {
    const newBoard = Array(4).fill().map(() => Array(4).fill(0));
    addNewTile(newBoard);
    addNewTile(newBoard);
    setBoard(newBoard);
    setScore(0);
    setGameOver(false);
    updateGameState({ board: newBoard, score: 0 });
  };

  const addNewTile = (board) => {
    const emptyCells = [];
    board.forEach((row, i) => {
      row.forEach((cell, j) => {
        if (cell === 0) emptyCells.push([i, j]);
      });
    });

    if (emptyCells.length > 0) {
      const [x, y] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      board[x][y] = Math.random() < 0.9 ? 2 : 4;
    }
  };

  const move = (direction) => {
    if (gameOver) return;

    let newBoard = JSON.parse(JSON.stringify(board));
    let moved = false;
    let newScore = score;

    switch (direction) {
      case 'left':
        for (let i = 0; i < 4; i++) {
          const row = newBoard[i].filter(cell => cell !== 0);
          for (let j = 0; j < row.length - 1; j++) {
            if (row[j] === row[j + 1]) {
              row[j] *= 2;
              newScore += row[j];
              row.splice(j + 1, 1);
              moved = true;
            }
          }
          const newRow = row.concat(Array(4 - row.length).fill(0));
          if (JSON.stringify(newBoard[i]) !== JSON.stringify(newRow)) moved = true;
          newBoard[i] = newRow;
        }
        break;
      case 'right':
        for (let i = 0; i < 4; i++) {
          const row = newBoard[i].filter(cell => cell !== 0);
          for (let j = row.length - 1; j > 0; j--) {
            if (row[j] === row[j - 1]) {
              row[j] *= 2;
              newScore += row[j];
              row.splice(j - 1, 1);
              moved = true;
            }
          }
          const newRow = Array(4 - row.length).fill(0).concat(row);
          if (JSON.stringify(newBoard[i]) !== JSON.stringify(newRow)) moved = true;
          newBoard[i] = newRow;
        }
        break;
      case 'up':
        for (let j = 0; j < 4; j++) {
          const column = [];
          for (let i = 0; i < 4; i++) {
            if (newBoard[i][j] !== 0) column.push(newBoard[i][j]);
          }
          for (let i = 0; i < column.length - 1; i++) {
            if (column[i] === column[i + 1]) {
              column[i] *= 2;
              newScore += column[i];
              column.splice(i + 1, 1);
              moved = true;
            }
          }
          const newColumn = column.concat(Array(4 - column.length).fill(0));
          for (let i = 0; i < 4; i++) {
            if (newBoard[i][j] !== newColumn[i]) moved = true;
            newBoard[i][j] = newColumn[i];
          }
        }
        break;
      case 'down':
        for (let j = 0; j < 4; j++) {
          const column = [];
          for (let i = 0; i < 4; i++) {
            if (newBoard[i][j] !== 0) column.push(newBoard[i][j]);
          }
          for (let i = column.length - 1; i > 0; i--) {
            if (column[i] === column[i - 1]) {
              column[i] *= 2;
              newScore += column[i];
              column.splice(i - 1, 1);
              moved = true;
            }
          }
          const newColumn = Array(4 - column.length).fill(0).concat(column);
          for (let i = 0; i < 4; i++) {
            if (newBoard[i][j] !== newColumn[i]) moved = true;
            newBoard[i][j] = newColumn[i];
          }
        }
        break;
      default:
        return;
    }

    if (moved) {
      addNewTile(newBoard);
      setBoard(newBoard);
      setScore(newScore);
      updateGameState({ board: newBoard, score: newScore });
      
      if (isGameOver(newBoard)) {
        setGameOver(true);
      }
    }
  };

  const isGameOver = (board) => {
    // Check if the board is full
    const isFull = board.every(row => row.every(cell => cell !== 0));
    if (!isFull) return false;

    // Check for possible moves horizontally
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[i][j] === board[i][j + 1]) return false;
      }
    }

    // Check for possible moves vertically
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === board[i + 1][j]) return false;
      }
    }

    return true;
  };

  const handleKeyPress = (e) => {
    const keyMap = {
      'ArrowLeft': 'left',
      'ArrowRight': 'right',
      'ArrowUp': 'up',
      'ArrowDown': 'down'
    };
    
    if (keyMap[e.key]) {
      move(keyMap[e.key]);
      e.preventDefault();
    }
  };

  return (
    <div className="flex flex-col min-h-screen pt-24 pb-12 relative z-0">
      {/* Header */}
      <div className="p-4 bg-gray-800">
        <div className="flex justify-between mb-4">
          <div className="text-2xl font-bold text-white">2048</div>
          <div className="text-xl text-white">Score: {score}</div>
        </div>
      </div>

      {/* Game Content - offset from header */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div 
          className="w-full max-w-lg mx-auto" 
          tabIndex={0} 
          onKeyDown={handleKeyPress}
          autoFocus
        >
          <div className="grid grid-cols-4 gap-2 bg-gray-200 p-2 rounded">
            {board.map((row, i) => 
              row.map((cell, j) => (
                <div
                  key={`${i}-${j}`}
                  className={`h-16 flex items-center justify-center 
                            rounded font-bold text-2xl
                            ${cell === 0 ? 'bg-gray-300' : 'bg-orange-300'}`}
                >
                  {cell !== 0 && cell}
                </div>
              ))
            )}
          </div>

          {gameOver && (
            <div className="text-center mt-4">
              <div className="text-xl font-bold mb-2">Game Over!</div>
              <button 
                onClick={initializeGame}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Play Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Game2048;